import * as yup from "yup";

import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Slide,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import {
  useAddNewEducationMutation,
  useDeleteCvEducationMutation,
  useEditCvEducationMutation,
} from "../../../lib/cvsSlice";

import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LocalLibraryOutlinedIcon from "@mui/icons-material/LocalLibraryOutlined";
import SaveIcon from "@mui/icons-material/Save";
import { cyan } from "@mui/material/colors";
import { monthNames } from "../../../config";
import { red } from "@mui/material/colors";
import useFormPersist from "react-hook-form-persist";
import { yupResolver } from "@hookform/resolvers/yup";

const setMonthToNumber = (month) => {
  switch (month) {
    case "January":
      return 1;
    case "February":
      return 2;
    case "March":
      return 3;
    case "April":
      return 4;
    case "May":
      return 5;
    case "June":
      return 6;
    case "July":
      return 7;
    case "August":
      return 8;
    case "September":
      return 9;
    case "October":
      return 10;
    case "November":
      return 11;
    case "December":
      return 12;

    default:
      return "neutral";
  }
};
//let startYear = new Date();
const educationSchema = yup.object().shape({
  institute: yup.string().required("Institution name is required"),
  achievement: yup.string().required("Achievement is required"),
  fieldOfStudy: yup.string().required("Field of study is required"),
  startMonth: yup.string().required("Start month is required"),
  startYear: yup.string().required("Start year is required"),
  endYear: yup
    .string().required("End year is required"),
  endMonth: yup
    .string()
    .when(["startYear", "endYear"], {
      is: (startYear, endYear) => startYear === endYear,
      then: yup
        .string()
        .test(
          "endMonth",
          "End month for the same year cannot be before start month",
          function (value, context) {
            var startMonth = context.parent.startMonth;
            var endMonth = value;
            console.log(startMonth + " " + endMonth);
            console.log(
              setMonthToNumber(endMonth) + " " + setMonthToNumber(startMonth)
            );
            return setMonthToNumber(endMonth) >= setMonthToNumber(startMonth);
          }
        ),
    })
    .required("End month is required"),
  description: yup.string().required("Education description is required"),
  excludeInCv: yup.boolean(),
});


const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});


const Education = ({ educations, cvId, props }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState();
  const [educationId, setEducationId] = useState();
  const [institute, setInstitute] = useState("");
  const [educationState, setEducationState] = useState(educations);
  const [logStartYear, setLogStartYear] = useState();
  const [logEndYear, setLogEndYear] = useState();
  const [localExcludeInCv, setLocalExcludeInCv] = useState({});


  const handleOpen = (mode) => {
    setOpen(true);
    setMode(mode);
  };

  const handleClose = () => {
    reset();
    setOpen(false);
  };


  const handleInputChange = (event, educationId, fieldName) => {
    const educationElement = educations.find((education) => education._id === educationId);
    educationElement[fieldName] = event.target.value;
  
    const updatedEducations = educations.map((education) => {
      if (education._id === educationId) {
        return { ...education, [fieldName]: event.target.value };
      }
      return education;
    });
    setEducationState(updatedEducations);
  };

  const handleToggleChange = async (newExcludeInCv, educationId, fieldName, updatedValue) => {
    try {
      const updatedEducations = educations.map((education) => {
        if (education._id === educationId) {
          return {
            ...education,
            [fieldName]: updatedValue,
          };
        }
        return education;
      });

      // Update the local state
      setLocalExcludeInCv((prevLocalExcludeInCv) => ({
        ...prevLocalExcludeInCv,
        [educationId]: newExcludeInCv,
      }));

      // Update localStorage to persist the toggle value across refreshes
      localStorage.setItem(`excludeInCv-${educationId}`, newExcludeInCv.toString());

      // Update the certificate on the backend
      await editCvEducation({
        cvId: cvId,
        educationId: educationId,
        education: updatedEducations.map((education) => ({
          ...education,
          excludeInCv: newExcludeInCv,
        })),
      });

      // Update the certs array in the state
      setEducationState(updatedEducations);
    } catch (error) {
      console.error('Error updating toggle:', error);
    }
  };


  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { values, errors },
  } = useForm({
    resolver: yupResolver(educationSchema),
    defaultValues: {
      institute: "",
      achievement: "",
      fieldOfStudy: "",
      startMonth: "",
      startYear: "",
      endMonth: "",
      endYear: "",
      description: "",
      excludeInCv: false,
    },
  });
  const [addNewEducation] = useAddNewEducationMutation();

  const submitResults = async (e) => {
    const newEducation = e;
    await addNewEducation({ cvId, education: [newEducation] }).unwrap();
    handleClose();
    educations.push(newEducation); // add the new education to the existing array
    toast.success("Education added successfully");
  };

  const [deleteCvEducation] = useDeleteCvEducationMutation();
  const handleRemove = async (item) => {
    await deleteCvEducation({ cvId, educationId: item }).unwrap();
    const updatedCvLanguages = educationState.filter((edu) => edu.id !== item);
    setEducationState(updatedCvLanguages);
    educations.splice(educations.findIndex((edu) => edu._id === item), 1);
    setEducationState([...educations]);
    toast.info("Education removed successfully");
  };

  const [editCvEducation] = useEditCvEducationMutation();
  const handleEdit = (educationId, mode) => {
    setOpen(true);
    setMode(mode);
    setEducationId(educationId);
  };
  
  const years = [];
  for (let year = 2023; year >= 1923; year--) {
    years.push(year);
  }

  //organising dates from latest to oldest
  const sortedEducation = educations
    .map((education) => {
      return {
        ...education,
        date:
          (new Date(
            education.endYear,
            setMonthToNumber(education.endMonth) - 1
          ),
            console.log(
              new Date(
                education.endYear,
                setMonthToNumber(education.endMonth) - 1
              )
            )),
          };
        })
        .sort(
          (a, b) =>
          new Date(b.endYear, setMonthToNumber(b.endMonth) - 1) -
          new Date(a.endYear, setMonthToNumber(a.endMonth) - 1)
     );

    useFormPersist("educations", {
      watch,
      setValue,
      storage: window.localStorage, // default window.sessionStorage
    });

    useEffect(() => {
      const data = localStorage.getItem("educations");
      if (data !== null) JSON.parse(data);
    }, []);

    useEffect(() => {
      localStorage.setItem("educations", JSON.stringify(educations));
    }, [educations]);

    useEffect(() => {
      const initialLocalExcludeInCv = {};
      educations.forEach((education) => {
        const savedState = localStorage.getItem(`excludeInCv-${education._id}`);
        initialLocalExcludeInCv[education._id] = savedState === 'true';
      });
    
      setLocalExcludeInCv(initialLocalExcludeInCv);
    }, [educations]);

    
    //Edit mode functionality
    const educationElement = educations.find((education) => education._id === educationId);

    const renderSingleElement = () => {
      return (
        <Grid
          container
          spacing={2}
          justifyContent="center"
          alignItems="center"
          sx={{ mt: 1.25 }}
        >
          <Grid item xs={12}>
            <TextField
              label="Institute"
              fullWidth
              autoFocus
              value={educationElement.institute}
              onChange={(e) => handleInputChange(e, educationId, "institute")}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Degree"
              fullWidth
              autoFocus
              value={educationElement.achievement}
              onChange={(e) => handleInputChange(e, educationId, "achievement")}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Field Of Study"
              fullWidth
              autoFocus
              value={educationElement.fieldOfStudy}
              onChange={(e) => handleInputChange(e, educationId, "fieldOfStudy")}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              select
              label="Start Year"
              fullWidth
              value={educationElement.startYear}
              onChange={(e) => handleInputChange(e, educationId, "startYear")}
            >
              {years.map((year) => (
                <MenuItem value={year} key={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              select
              label="Start Month"
              fullWidth
              value={educationElement.startMonth}
              onChange={(e) => handleInputChange(e, educationId, "startMonth")}
            >
              {monthNames.map((month) => (
                <MenuItem value={month.name} key={month.name}>
                  {month.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              select
              label="End Year"
              fullWidth
              value={educationElement.endYear}
              onChange={(e) => handleInputChange(e, educationId, "endYear")}
            >
              {years.map((year) => (
                <MenuItem value={year} key={year}>
                  {year}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField
              select
              label="End Month"
              fullWidth
              value={educationElement.endMonth}
              onChange={(e) => handleInputChange(e, educationId, "endMonth")}
            >
              {monthNames.map((month) => (
                <MenuItem value={month.name} key={month.name}>
                  {month.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description"
              multiline
              maxRows={6}
              fullWidth
              value={educationElement.description}
              onChange={(e) => handleInputChange(e, educationId, "description")}
            />
          </Grid>
        </Grid>
      );
    };
    

  const submitEditResults = async () => {
      await editCvEducation({
        cvId,
        educationId,
        education: [educationElement],
      }).unwrap();
      handleClose();
      toast.info("Education updated successfully");
    };

    
  return (
    <div id="Education">
      <ToastContainer autoClose={1500} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper sx={{ mt: 2.5, p: 1.875 }}
          >
            <Grid
              container
              spacing={2}
              justifyContent="center"
              alignItems="center"
              sx={{ mt: 0.625 }}

            >
              <Grid item xs={12}>
                <Grid
                  container
                  spacing={2}
                  // justifyContent="center"
                  // alignItems="center"
                >
                  <Grid item xs={9}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      component="div"
                      sx={{ p: 2, pb: 0 }}
                    >
                      Education &nbsp;
                      <Tooltip
                        disableFocusListener
                        disableTouchListener
                        title="This section is mandatory"
                      >
                        <span sx={{ color: "red" }}
                        >*</span>
                      </Tooltip>
                    </Typography>
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton
                      aria-label="add"
                      onClick={() => handleOpen("add")}
                    >
                      <AddIcon color="primary" />
                    </IconButton>
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton
                      aria-label="edit"
                      onClick={() => handleOpen("edit")}
                    >
                      <EditIcon color="primary" />
                    </IconButton>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                {sortedEducation?.map((education) => (
                  <List key={education._id} sx={{ p: 0 }}
>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ backgroundColor: cyan[700] }}
                        >
                          <LocalLibraryOutlinedIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        sx={{ pl: 1.25 }}
                        primary={
                          <Typography variant="subtitle2" component="div">
                           Institute: {education.institute}
                          </Typography>
                        }
                        secondary={
                          <div>
                            <Typography variant="caption" display="block">
                             Degree: {education.achievement}
                            </Typography>
                            <Typography variant="caption" display="block">
                             field Of Study: {education.fieldOfStudy}
                            </Typography>
                            <Typography variant="caption" display="block">
                             Start Date: {education.startMonth} {education.startYear}
                             <Typography variant="caption" display="block">
                             End date:  {education.endMonth} {education.endYear}
                             </Typography>
                            </Typography>
                            <Typography variant="caption" display="block">
                             Description: {education.description}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Exclude in cv: {education.excludeInCv ? "Yes" : "No"}
                            </Typography>
                          </div>
                        }
                      />

                  <Grid item xs={12} sm={3} sx={{ textAlign: 'right', pr: 5 }}>
                    <FormControl>
                    <FormHelperText error={!!errors.excludeInCv}>
                      {errors?.excludeInCv ? errors.excludeInCv?.message : ''}
                    </FormHelperText>
                    <Controller
                    key = {education._id}
                    name={`excludeInCv-${education._id}`}
                    control={control}
                    render={({ field: { onChange, value, ...field } }) => (
                      <Tooltip title="This will exclude the section from the CV">
                        <FormControlLabel
                          sx={{ m: 0 }}

                          control={
                            <Switch
                            onChange={(e) => {
                              const newExcludeInCv = e.target.checked;
                            
                                setLocalExcludeInCv((prevLocalExcludeInCv) => ({
                                  ...prevLocalExcludeInCv,
                                  [education._id]: newExcludeInCv,
                                }));
                              
                                handleToggleChange(newExcludeInCv, education._id, 'excludeInCv');
                              }}
                              checked={localExcludeInCv[education._id] || false}
                              {...field}
                              ref={null}
                            />

                          }
                        />
                      </Tooltip>
                    )}
                   />
                  </FormControl>
                  </Grid>
                    </ListItem>
                  </List>
                ))}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
      >
        {/*    start from here */}
        {mode === "add" && (
          <form autoComplete="off" onSubmit={handleSubmit(submitResults)}>
            <DialogTitle>Add Education</DialogTitle>
            <DialogContent dividers={true}>
              <Grid
                container
                spacing={2}
                justifyContent="center"
                sx={{ mt: 1.25 }}
              >
                <Grid item xs={12}>
                  <Controller
                    name="institute"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Institute"
                        fullWidth
                        autoFocus
                        value={field.value} // Set the value using field.value
                        onChange={(e) => {
                          field.onChange(e); // Trigger the onChange event in the form state
                          setInstitute(e.target.value); // Optional: Set the local state if needed
                        }}          

                        error={!!errors.institute}
                        helperText={
                          errors?.institute ? errors.institute?.message : ""
                        }
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Controller
                    name="achievement"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Degree"
                        fullWidth
                        value={field.value} // Set the value using field.value
                        onChange={(e) => {
                          field.onChange(e); // Trigger the onChange event in the form state
                          setInstitute(e.target.value); // Optional: Set the local state if needed
                        }}          
                        autoFocus
                        error={!!errors.achievement}
                        helperText={
                          errors?.achievement ? errors.achievement?.message : ""
                        }
                      ></TextField>
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="fieldOfStudy"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Field Of Study"
                        fullWidth
                        autoFocus
                        value={field.value} // Set the value using field.value
                        onChange={(e) => {
                          field.onChange(e); // Trigger the onChange event in the form state
                          setInstitute(e.target.value); // Optional: Set the local state if needed
                        }}          
                        error={!!errors.fieldOfStudy}
                        helperText={
                          errors?.fieldOfStudy
                            ? errors.fieldOfStudy?.message
                            : ""
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6}>
                  <Controller
                    name="startYear"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        label="Start Year"
                        fullWidth
                        SelectProps={{
                          MenuProps: {
                            anchorOrigin: {
                              vertical: "bottom",
                              horizontal: "center",
                            },
                          },
                        }}
                        logStartYear={logStartYear}
                        error={!!errors.startYear}
                        helperText={
                          errors?.startYear ? errors.startYear?.message : ""
                        }
                        value={values?.startYear} // Update value prop
                        onChange={(e) => {
                          setLogStartYear(e.target.value);
                          field.onChange(e);
                        }}
                      >
                        {years.map((year) => (
                          <MenuItem value={year} key={year}>
                            {year}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                <Grid item xs={6}>
                  <Controller
                    name="startMonth"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        label="Start Month"
                        fullWidth
                        SelectProps={{
                          MenuProps: {
                            anchorOrigin: {
                              vertical: "bottom",
                              horizontal: "center",
                            },
                          },
                        }}
                        error={!!errors.startMonth}
                        helperText={
                          errors?.startMonth ? errors.startMonth?.message : ""
                        }
                      >
                        {monthNames.map((month) => (
                          <MenuItem value={month.name} key={month.name}>
                            {month.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>

                <Grid item xs={6}>
                  <Controller
                    name="endYear"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => {
                      const filteredYears = years.filter(year => year >= logStartYear);
                      return (
                        <TextField
                          {...field}
                          select
                          label="End Year"
                          fullWidth
                          SelectProps={{
                            MenuProps: {
                              anchorOrigin: {
                                vertical: "bottom",
                                horizontal: "center",
                              },
                            },
                          }}
                          logEndYear={logEndYear}
                          error={!!errors.endYear}
                          helperText={
                            errors?.endYear ? errors.endYear?.message : ""
                          }
                          value={values?.endYear} // Update value prop
                          onChange={(e) => {
                            setLogEndYear(e.target?.value);
                            field.onChange(e);
                          }}
                        >
                          {filteredYears.map((year) => (
                            <MenuItem value={year} key={year}>
                              {year}
                            </MenuItem>
                          ))}
                        </TextField>
                      );
                    }}
                  />
                </Grid>


                <Grid item xs={6}>
                  <Controller
                    name="endMonth"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        label="End Month"
                        fullWidth
                        SelectProps={{
                          MenuProps: {
                            anchorOrigin: {
                              vertical: "bottom",
                              horizontal: "center",
                            },
                          },
                        }}
                        error={!!errors.endMonth}
                        helperText={
                          errors?.endMonth ? errors.endMonth?.message : ""
                        }
                      >
                        {monthNames.map((month) => (
                          <MenuItem value={month.name} key={month.name}>
                            {month.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>


                <Grid item xs={12}>
                  <Controller
                    name="description"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Description"
                        multiline
                        maxRows={6}
                        fullWidth
                        error={!!errors.description}
                        helperText={
                          errors?.description ? errors.description?.message : ""
                        }
                      />
                    )}
                  />
                </Grid>

              </Grid>
            </DialogContent>
            <DialogActions>
              <Grid
                container
                spacing={2}
                sx={{ m: 1.25 }}

                justifyContent="flex-end"
              >
                <Grid item xs={12}>
                  <Stack alignItems="right" direction="row" spacing={3}>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<SaveIcon />}
                      type="submit"
                    >
                      Save
                    </Button>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<CancelIcon />}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        )}
        {mode === "edit" && (
          <form>
            <DialogTitle>Edit/Remove Education</DialogTitle>
            <DialogContent dividers={true}>
              <Grid
                container
                spacing={2}
                justifyContent="center"
                alignItems="center"
                sx={{ mt: 0.625 }}

              >
                <Grid item xs={12}>
                  {educations?.map((education) => (
                    <List key={education._id} sx={{ p: 0 }}
>
                      <ListItem
                        sx={{ pl: 1.25 }}
                        secondaryAction={
                          <Stack alignItems="right" direction="row">
                            <IconButton
                              aria-label="delete"
                              onClick={() =>
                                handleEdit(education._id, "editnew")
                              }
                            >
                              <EditIcon color="primary" />
                            </IconButton>
                            <IconButton
                              aria-label="delete"
                              onClick={() => handleRemove(education._id)}
                            >
                              <DeleteIcon sx={{ color: red[400] }}
 />
                            </IconButton>
                          </Stack>
                        }
                      >
                        <ListItemText
                         sx={{ pl: 1.25 }}
                          primary={
                            <Typography variant="subtitle2" component="div">
                              Institute: {education.institute}
                            </Typography>
                          }
                          secondary={
                            <div>
                              <Typography variant="caption" display="block">
                             Degree: {education.achievement}
                            </Typography>
                            <Typography variant="caption" display="block">
                             field Of Study: {education.fieldOfStudy}
                            </Typography>
                            <Typography variant="caption" display="block">
                             Start Date: {education.startMonth} {education.startYear}
                             <Typography variant="caption" display="block">
                             End date:  {education.endMonth} {education.endYear}
                             </Typography>
                            </Typography>
                            <Typography variant="caption" display="block">
                             Description: {education.description}
                            </Typography>
                            <Typography variant="caption" display="block">
                                Exclude in cv: {education.excludeInCv ? "Yes" : "No"}
                            </Typography>
                            </div>
                          }
                        />
                      </ListItem>
                    </List>
                  ))}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Grid
                container
                spacing={2}
                sx={{ m: 1.25 }}

                justifyContent="flex-end"
              >
                <Grid item xs={12}>
                  <Stack alignItems="right" direction="row" spacing={3}>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<SaveIcon />}
                      type="submit"
                      disabled
                    >
                      Save
                    </Button>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<CancelIcon />}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        )}

        {mode === "editnew" && (
          <form
            autoComplete="off"
            onSubmit={() => {
              submitEditResults();
            }}
          >
            <DialogTitle>Edit Education</DialogTitle>
            <DialogContent dividers={true}>
              {renderSingleElement()}
            </DialogContent>
            <DialogActions>
              <Grid
                container
                spacing={2}
                sx={{ m: 1.25 }}

                justifyContent="flex-end"
              >
                <Grid item xs={12}>
                  <Stack alignItems="right" direction="row" spacing={3}>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<SaveIcon />}
                      type="submit"
                    >
                      Save
                    </Button>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<CancelIcon />}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        )}
      </Dialog>
    </div>
  );
};

export default Education;
